import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  Select,
  DatePicker,
  DateRangePicker,
  Tag,
  Space,
  Popconfirm,
  MessagePlugin,
  type PrimaryTableCol,
} from 'tdesign-react';
import { AddIcon, EditIcon, DeleteIcon, SearchIcon, PlusIcon, UserAddIcon } from 'tdesign-icons-react';
import { tenantApi, businessCardLevelApi, userApi } from '../../api';
import CoverImageUploader from '../../components/CoverImageUploader';
import type { Tenant } from '../../types';

const { FormItem } = Form;
const { Option } = Select;

const tenantTypeMap: Record<number, { label: string; theme: 'primary' | 'success' }> = {
  1: { label: '企业', theme: 'primary' },
  2: { label: '个人', theme: 'success' },
};

const tenantStatusMap: Record<number, { label: string; theme: 'default' | 'success' | 'warning' }> = {
  0: { label: '未开始', theme: 'default' },
  1: { label: '有效', theme: 'success' },
  2: { label: '已过期', theme: 'warning' },
};

interface PackageLevel {
  id: number;
  levelName: string;
  levelType: number;
  levelOrigPrice: number;
  levelSalesPrice: number;
  levelUserNum: number;
  sortOrder: number;
  status: number;
}

interface AdminUser {
  id: number;
  username: string;
  nickname?: string;
  phone?: string;
  email?: string;
}

export default function TenantManage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [mergeVisible, setMergeVisible] = useState(false);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [searchForm] = Form.useForm();
  const formInstanceRef = useRef<any>(null);
  const mergeFormInstanceRef = useRef<any>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [packageLevels, setPackageLevels] = useState<PackageLevel[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<PackageLevel | null>(null);
  const [mergeSelectedPackage, setMergeSelectedPackage] = useState<PackageLevel | null>(null);
  const [userSearchKeyword, setUserSearchKeyword] = useState('');
  const [userLoading, setUserLoading] = useState(false);
  const [mergeAvatar, setMergeAvatar] = useState('');

  const hasSearchedUsers = useRef(false);

  const fetchTenants = async (params: { pageNum?: number; pageSize?: number } = {}) => {
    setLoading(true);
    try {
      const rawSearchParams = searchForm.getFieldsValue?.(true) || {};
      const searchParams = Object.keys(rawSearchParams).reduce((acc, key) => {
        const value = rawSearchParams[key];
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const res = await tenantApi.getPageList({
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        ...searchParams,
        ...params,
      });
      if (res.code === 200) {
        const list = res.data?.list || res.data?.records || [];
        setTenants(list);
        setPagination({
          ...pagination,
          total: res.data?.total || 0,
          ...(params.pageNum && { current: params.pageNum }),
          ...(params.pageSize && { pageSize: params.pageSize }),
        });
      }
    } catch (error) {
      MessagePlugin.error('获取租户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchPackageLevels = async () => {
    try {
      const res = await businessCardLevelApi.getPageList({ pageNum: 1, pageSize: 50, status: 1 });
      if (res.code === 200) {
        setPackageLevels(res.data?.list || res.data?.records || []);
      }
    } catch {
      console.error('获取套餐列表失败');
    }
  };

  const searchUsers = async (keyword: string) => {
    setUserLoading(true);
    try {
      const res = await userApi.getList({
        pageNum: 1,
        pageSize: 50,
        username: keyword || undefined,
      });
      if (res.code === 200) {
        setUsers(res.data?.list || res.data?.records || []);
      }
    } catch {
      console.error('获取用户列表失败');
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchPackageLevels();
  }, []);

  const handleUserPopupVisibleChange = (visible: boolean) => {
    if (visible && !hasSearchedUsers.current) {
      hasSearchedUsers.current = true;
      searchUsers('');
    }
  };

  const handleUserSearch = (keyword: string) => {
    setUserSearchKeyword(keyword);
    searchUsers(keyword);
  };

  const resetFormState = () => {
    setCurrentTenant(null);
    setSelectedPackage(null);
    if (formInstanceRef.current) {
      formInstanceRef.current.reset();
    }
  };

  const resetMergeFormState = () => {
    setMergeSelectedPackage(null);
    setMergeAvatar('');
    if (mergeFormInstanceRef.current) {
      mergeFormInstanceRef.current.reset();
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchTenants({ pageNum: 1 });
  };

  const handleReset = () => {
    searchForm.reset();
    setPagination({ ...pagination, current: 1 });
    fetchTenants({ pageNum: 1 });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination({ ...pagination, current: page, pageSize });
    fetchTenants({ pageNum: page, pageSize });
  };

  const handleAdd = () => {
    resetFormState();
    hasSearchedUsers.current = false;
    setUsers([]);
    setVisible(true);
  };

  const handleMergeAdd = () => {
    resetMergeFormState();
    setMergeVisible(true);
  };

  const handleEdit = async (record: Tenant) => {
    try {
      const res = await tenantApi.getById(record.id);
      if (res.code === 200) {
        const start = res.data?.startTime || '';
        const end = res.data?.endTime || '';
        const dateRange = (start || end) ? [start, end] : undefined;

        const pkg = packageLevels.find((p) => p.levelSalesPrice === res.data?.tenantPrice || p.id === res.data?.tenantLevel) || null;
        setSelectedPackage(pkg);

        setCurrentTenant({
          ...res.data,
          dateRange,
          userId: res.data?.userId,
        });
        setVisible(true);
      } else {
        MessagePlugin.error('获取租户详情失败');
      }
    } catch {
      MessagePlugin.error('获取租户详情失败');
    }
  };

  const handlePackageChange = (levelId: number) => {
    const pkg = packageLevels.find((p) => p.id === levelId);
    setSelectedPackage(pkg || null);
    if (pkg && formInstanceRef.current) {
      formInstanceRef.current.setFieldsValue({
        tenantLevel: levelId,
        tenantType: pkg.levelType,
        tenantPrice: pkg.levelSalesPrice,
      });
    }
  };

  const handleMergePackageChange = (levelId: number) => {
    const pkg = packageLevels.find((p) => p.id === levelId);
    setMergeSelectedPackage(pkg || null);
    if (pkg && mergeFormInstanceRef.current) {
      mergeFormInstanceRef.current.setFieldsValue({
        tenantLevel: levelId,
        tenantType: pkg.levelType,
        tenantPrice: pkg.levelSalesPrice,
      });
    }
  };

  const handleFormSubmit = async () => {
    try {
      const validateResult = formInstanceRef.current?.validate();
      if (validateResult !== true && Object.keys(validateResult).length > 0) {
        MessagePlugin.warning('请完善表单信息');
        return;
      }
      const values = formInstanceRef.current?.getFieldsValue(true);
      const { dateRange, tenantLevel, tenantType, tenantPrice, ...restValues } = values;

      const submitData: any = {
        ...restValues,
        tenantLevel: Number(tenantLevel),
        tenantType: Number(tenantType),
        tenantPrice: Number(tenantPrice),
        startTime: Array.isArray(dateRange) ? dateRange[0] : '',
        endTime: Array.isArray(dateRange) ? dateRange[1] : '',
      };

      if (currentTenant) {
        submitData.userId = currentTenant.userId;
        const res = await tenantApi.update(currentTenant.id, submitData);
        if (res.code === 200) {
          MessagePlugin.success('更新成功');
          setVisible(false);
          resetFormState();
          fetchTenants();
        } else {
          MessagePlugin.error(res.message || '操作失败');
        }
      } else {
        submitData.userId = Number(values.userId);
        const res = await tenantApi.create(submitData);
        if (res.code === 200) {
          MessagePlugin.success('创建成功');
          setVisible(false);
          resetFormState();
          fetchTenants();
        } else {
          MessagePlugin.error(res.message || '操作失败');
        }
      }
    } catch (error) {
      MessagePlugin.error('提交失败，请稍后重试');
    }
  };

  const handleMergeFormSubmit = async () => {
    try {
      const validateResult = mergeFormInstanceRef.current?.validate();
      if (validateResult !== true && Object.keys(validateResult).length > 0) {
        MessagePlugin.warning('请完善表单信息');
        return;
      }
      const values = mergeFormInstanceRef.current?.getFieldsValue(true);
      const { dateRange, tenantLevel, tenantType, tenantPrice, ...restValues } = values;

      const submitData: any = {
        ...restValues,
        avatar: mergeAvatar || '',
        tenantLevel: Number(tenantLevel),
        tenantType: Number(tenantType),
        tenantPrice: Number(tenantPrice),
        startTime: Array.isArray(dateRange) ? dateRange[0] : '',
        endTime: Array.isArray(dateRange) ? dateRange[1] : '',
      };

      const res = await tenantApi.createWithUser(submitData);
      if (res.code === 200) {
        MessagePlugin.success('合并创建成功');
        setMergeVisible(false);
        resetMergeFormState();
        fetchTenants();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch (error) {
      MessagePlugin.error('提交失败，请稍后重试');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await tenantApi.delete(id);
      MessagePlugin.success('删除成功');
      setSelectedRowKeys([]);
      fetchTenants();
    } catch (error) {
      MessagePlugin.error('删除失败');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      MessagePlugin.warning('请先选择要删除的数据');
      return;
    }
    try {
      await tenantApi.deleteBatch(selectedRowKeys as number[]);
      MessagePlugin.success('批量删除成功');
      setSelectedRowKeys([]);
      fetchTenants();
    } catch (error) {
      MessagePlugin.error('批量删除失败');
    }
  };

  const toggleSelectRow = (rowKey: number) => {
    setSelectedRowKeys((prev) => {
      if (prev.includes(rowKey)) {
        return prev.filter((id) => id !== rowKey);
      }
      return [...prev, rowKey];
    });
  };

  const packageCard = useMemo(() => {
    if (!selectedPackage) return null;
    const { levelName, levelType, levelOrigPrice, levelSalesPrice, levelUserNum } = selectedPackage;
    const isEnterprise = levelType === 1;
    return (
      <div className="mb-4 rounded-xl overflow-hidden border shadow-sm" style={{
        borderColor: isEnterprise ? 'rgba(0,117,255,0.15)' : 'rgba(0,180,116,0.15)',
        background: isEnterprise
          ? 'linear-gradient(135deg, rgba(0,117,255,0.06), rgba(0,117,255,0.02))'
          : 'linear-gradient(135deg, rgba(0,180,116,0.06), rgba(0,180,116,0.02))',
      }}>
        <div className="flex items-center justify-between px-4 py-3" style={{
          background: isEnterprise
            ? 'linear-gradient(90deg, rgba(0,117,255,0.14), rgba(0,117,255,0))'
            : 'linear-gradient(90deg, rgba(0,180,116,0.14), rgba(0,180,116,0))',
        }}>
          <div className="flex items-center gap-2">
            <span className="w-2 h-6 rounded-full" style={{ background: isEnterprise ? '#0075ff' : '#00b474' }} />
            <div className="font-semibold text-base" style={{ color: isEnterprise ? '#0075ff' : '#00a86a' }}>
              {levelName}
            </div>
            <Tag theme={isEnterprise ? 'primary' : 'success'} variant="light" size="small">
              {isEnterprise ? '企业' : '个人'}
            </Tag>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 px-4 py-3">
          <div>
            <div className="text-xs text-gray-400 mb-0.5">原价</div>
            <div className="text-sm text-gray-400 line-through">¥{levelOrigPrice?.toFixed(2) || '0.00'}/年</div>
          </div>
          <div>
            <div className="text-xs text-gray-400 mb-0.5">售价</div>
            <div className="text-base font-semibold" style={{ color: isEnterprise ? '#0075ff' : '#00a86a' }}>
              ¥{levelSalesPrice?.toFixed(2) || '0.00'}/年
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400 mb-0.5">包含人数</div>
            <div className="text-sm font-medium text-gray-700">{levelUserNum} 人</div>
          </div>
        </div>
      </div>
    );
  }, [selectedPackage]);

  const mergePackageCard = useMemo(() => {
    if (!mergeSelectedPackage) return null;
    const { levelName, levelType, levelOrigPrice, levelSalesPrice, levelUserNum } = mergeSelectedPackage;
    const isEnterprise = levelType === 1;
    return (
      <div className="mb-4 rounded-xl overflow-hidden border shadow-sm" style={{
        borderColor: isEnterprise ? 'rgba(0,117,255,0.15)' : 'rgba(0,180,116,0.15)',
        background: isEnterprise
          ? 'linear-gradient(135deg, rgba(0,117,255,0.06), rgba(0,117,255,0.02))'
          : 'linear-gradient(135deg, rgba(0,180,116,0.06), rgba(0,180,116,0.02))',
      }}>
        <div className="flex items-center justify-between px-4 py-3" style={{
          background: isEnterprise
            ? 'linear-gradient(90deg, rgba(0,117,255,0.14), rgba(0,117,255,0))'
            : 'linear-gradient(90deg, rgba(0,180,116,0.14), rgba(0,180,116,0))',
        }}>
          <div className="flex items-center gap-2">
            <span className="w-2 h-6 rounded-full" style={{ background: isEnterprise ? '#0075ff' : '#00b474' }} />
            <div className="font-semibold text-base" style={{ color: isEnterprise ? '#0075ff' : '#00a86a' }}>
              {levelName}
            </div>
            <Tag theme={isEnterprise ? 'primary' : 'success'} variant="light" size="small">
              {isEnterprise ? '企业' : '个人'}
            </Tag>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 px-4 py-3">
          <div>
            <div className="text-xs text-gray-400 mb-0.5">原价</div>
            <div className="text-sm text-gray-400 line-through">¥{levelOrigPrice?.toFixed(2) || '0.00'}/年</div>
          </div>
          <div>
            <div className="text-xs text-gray-400 mb-0.5">售价</div>
            <div className="text-base font-semibold" style={{ color: isEnterprise ? '#0075ff' : '#00a86a' }}>
              ¥{levelSalesPrice?.toFixed(2) || '0.00'}/年
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400 mb-0.5">包含人数</div>
            <div className="text-sm font-medium text-gray-700">{levelUserNum} 人</div>
          </div>
          {/* <div>
            <div className="text-xs text-gray-400 mb-0.5">套餐编号</div>
            <div className="text-sm font-medium text-gray-700">#{mergeSelectedPackage.id}</div>
          </div> */}
        </div>
      </div>
    );
  }, [mergeSelectedPackage]);

  const columns: PrimaryTableCol<Tenant>[] = [
    {
      colKey: 'selection',
      title: () => (
        <input
          type="checkbox"
          checked={tenants.length > 0 && tenants.every((item) => selectedRowKeys.includes(item.id))}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys(tenants.map((o) => o.id));
            } else {
              setSelectedRowKeys([]);
            }
          }}
          className="w-4 h-4 rounded border-gray-300"
        />
      ),
      width: 50,
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={selectedRowKeys.includes(row.id)}
          onChange={() => toggleSelectRow(row.id)}
          className="w-4 h-4 rounded border-gray-300"
        />
      ),
    },
    {
      colKey: 'tenantName',
      title: '租户名称',
      width: 150,
      cell: ({ row }: { row: Tenant }) => row.tenantName || '-',
    },
    {
      colKey: 'tenantType',
      title: '租户类型',
      width: 100,
      cell: ({ row }: { row: Tenant }) => {
        const info = tenantTypeMap[row.tenantType];
        return info ? <Tag theme={info.theme} variant="light">{info.label}</Tag> : '-';
      },
    },
    {
      colKey: 'tenantLevel',
      title: '套餐等级',
      width: 140,
      cell: ({ row }: { row: Tenant }) => {
        const pkg = packageLevels.find((p) => p.id === row.tenantLevel);
        if (pkg) {
          return <Tag theme={pkg.levelType === 1 ? 'primary' : 'success'} variant="light">{pkg.levelName}</Tag>;
        }
        const info = tenantTypeMap[row.tenantLevel];
        return info ? <Tag theme={(info as any).theme} variant="light">{info.label}</Tag> : '-';
      },
    },
    {
      colKey: 'tenantPrice',
      title: '租用价格',
      width: 120,
      cell: ({ row }: { row: Tenant }) => (
        <span className="font-medium text-[#1c5bff]">¥{row.tenantPrice?.toFixed(2) || '0.00'}</span>
      ),
    },
    {
      colKey: 'tenantStatus',
      title: '租户状态',
      width: 100,
      cell: ({ row }: any) => {
        const info = tenantStatusMap[row.tenantStatus];
        return info ? <Tag theme={info.theme} variant="light">{info.label}</Tag> : '-';
      },
    },
    // { colKey: 'userId', title: '关联用户', width: 120 },
    {
      colKey: 'invitedTime',
      title: '邀请时间',
      width: 170,
      cell: ({ row }: { row: Tenant }) => row.invitedTime || '-',
    },
    {
      colKey: 'startTime',
      title: '开始时间',
      width: 170,
      cell: ({ row }: { row: Tenant }) => row.startTime || '-',
    },
    {
      colKey: 'endTime',
      title: '结束时间',
      width: 170,
      cell: ({ row }: { row: Tenant }) => row.endTime || '-',
    },
    {
      colKey: 'action',
      title: '操作',
      width: 150,
      fixed: 'right',
      cell: ({ row }: { row: Tenant }) => (
        <Space>
          <Button theme="primary" variant="text" size="small" onClick={() => handleEdit(row)} icon={<EditIcon />}>
            编辑
          </Button>
          <Popconfirm content="确定删除该租户吗？" onConfirm={() => handleDelete(row.id)}>
            <Button theme="danger" variant="text" size="small" icon={<DeleteIcon />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold">租户管理</h1>
        <Space>
          <Button theme="default" onClick={handleMergeAdd} icon={<UserAddIcon />}>
            合并创建
          </Button>
          <Button theme="primary" onClick={handleAdd} icon={<AddIcon />}>
            新增租户
          </Button>
          <Popconfirm content="确定批量删除选中的租户吗？" onConfirm={handleBatchDelete}>
            <Button theme="danger" disabled={selectedRowKeys.length === 0} icon={<DeleteIcon />}>
              批量删除
            </Button>
          </Popconfirm>
          {selectedRowKeys.length > 0 && (
            <span className="text-sm text-gray-500">
              已选择 <span className="text-[#1c5bff] font-medium">{selectedRowKeys.length}</span> 项
            </span>
          )}
        </Space>
      </div>

      {/* 搜索区域 */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
        <Form form={searchForm} layout="inline">
          <FormItem name="tenantType" label="租户类型">
            <Select placeholder="请选择租户类型" clearable style={{ width: 150 }}>
              <Option value={1}>企业</Option>
              <Option value={2}>个人</Option>
            </Select>
          </FormItem>
          <FormItem>
            <Button theme="primary" onClick={handleSearch} icon={<SearchIcon />}>
              查询
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={handleReset}>
              重置
            </Button>
          </FormItem>
        </Form>
      </div>

      {/* 表格区域 */}
      <div className="bg-white rounded-lg shadow-sm">
        <Table
          rowKey="id"
          data={tenants}
          columns={columns}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showJumper: true,
            onChange: (pageInfo) => handlePageChange(pageInfo.current, pageInfo.pageSize),
          }}
          selectOnRowClick
          selectedRowKeys={selectedRowKeys}
          onSelectChange={(val: any) => setSelectedRowKeys(val)}
        />
      </div>

      {/* 新增/编辑 弹窗 */}
      <Dialog
        header={currentTenant ? '编辑租户' : '新增租户'}
        visible={visible}
        onClose={() => {
          setVisible(false);
          resetFormState();
        }}
        onConfirm={handleFormSubmit}
        confirmBtn="保存"
        width={900}
        key={currentTenant ? `edit-${currentTenant.id}` : 'add-new'}
      >
        <Form
          ref={formInstanceRef}
          labelWidth={120}
          initialData={currentTenant || undefined}
        >
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            {!currentTenant && (
              <>
                <div className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100 flex items-center gap-2">
                  <PlusIcon style={{ color: '#0075ff' }} />
                  选择关联用户
                </div>
                <FormItem label="关联用户" name="userId" rules={[{ required: true, message: '请选择关联用户' }]}>
                  <Select
                    placeholder="请输入关键词搜索后选择用户"
                    filterable
                    loading={userLoading}
                    onSearch={handleUserSearch}
                    onPopupVisibleChange={handleUserPopupVisibleChange}
                    valueType="value"
                  >
                    {users.length === 0 && !userLoading ? (
                      <Option disabled value="" className="text-center text-gray-400">
                        请输入用户名搜索
                      </Option>
                    ) : (
                      users.map((u) => (
                        <Option key={u.id} value={u.id}>
                          <div className="flex items-center gap-2">
                            {/* <span className="font-medium">{u.nickname || u.username}</span> */}
                            <span className="text-xs text-gray-400">用户名：{u.username}</span>
                            {u.phone && <span className="text-xs text-gray-400">手机号：{u.phone}</span>}
                          </div>
                        </Option>
                      ))
                    )}
                  </Select>
                </FormItem>
              </>
            )}

            <div className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100 mt-4 flex items-center gap-2">
              <PlusIcon style={{ color: '#0075ff' }} />
              租户信息
            </div>
            <FormItem label="套餐等级" name="tenantLevel" rules={[{ required: true, message: '请选择套餐等级' }]}>
              <Select placeholder="请选择套餐等级" filterable onChange={handlePackageChange}>
                {packageLevels.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.levelName}（{p.levelType === 1 ? '企业' : '个人'}，¥{p.levelSalesPrice}/年）
                  </Option>
                ))}
              </Select>
            </FormItem>

            {packageCard}

            <FormItem label="租户名称" name="tenantName" rules={[{ required: true, message: '请输入租户名称' }]}>
              <Input placeholder="请输入租户名称（企业名称等）" />
            </FormItem>

            <FormItem label="租户类型" name="tenantType" rules={[{ required: true, message: '请选择租户类型' }]}>
              <Select placeholder="请选择租户类型" disabled={!!selectedPackage}>
                <Option value={1}>企业</Option>
                <Option value={2}>个人</Option>
              </Select>
            </FormItem>

            <FormItem label="租用价格" name="tenantPrice" rules={[{ required: true, message: '请选择套餐等级' }]}>
              <Input placeholder="选择套餐后自动填充" disabled value={selectedPackage ? String(selectedPackage.levelSalesPrice) : ''} />
            </FormItem>

            <FormItem label="邀请时间" name="invitedTime" rules={[{ required: true, message: '请选择邀请时间' }]}>
              <DatePicker placeholder="请选择邀请时间" format="YYYY-MM-DD HH:mm:ss" />
            </FormItem>

            <FormItem label="租用时间" name="dateRange" rules={[{ required: true, message: '请选择租用时间' }]}>
              <DateRangePicker
                placeholder={['开始时间', '结束时间']}
                format="YYYY-MM-DD HH:mm:ss"
              />
            </FormItem>
          </div>
        </Form>
      </Dialog>

      {/* 合并创建 弹窗 */}
      <Dialog
        header="合并创建（线下签约流程）"
        visible={mergeVisible}
        onClose={() => {
          setMergeVisible(false);
          resetMergeFormState();
        }}
        onConfirm={handleMergeFormSubmit}
        confirmBtn="保存"
        width={760}
        key="merge-create-new"
      >
        <Form
          ref={mergeFormInstanceRef}
          labelWidth={120}
          initialData={{ status: 1 }}
        >
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            <div className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100 flex items-center gap-2">
              <PlusIcon style={{ color: '#0075ff' }} />
              用户信息（新租户负责人）
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
              <FormItem label="头像" name="avatar" className="md:col-span-2">
                <CoverImageUploader
                  simple
                  value={mergeAvatar}
                  onChange={(value) => setMergeAvatar(Array.isArray(value) ? value[0] : value)}
                />
              </FormItem>
              <FormItem label="用户名" name="username" rules={[{ required: true, message: '请输入用户名' }]}>
                <Input placeholder="请输入登录用户名" />
              </FormItem>
              <FormItem label="密码" name="password" rules={[{ required: true, message: '请输入密码' }]}>
                <Input placeholder="请输入初始密码" type="password" />
              </FormItem>
              <FormItem label="昵称" name="nickname">
                <Input placeholder="请输入昵称/姓名" />
              </FormItem>
              <FormItem label="手机号" name="phone" rules={[{ required: true, message: '请输入手机号' }]}>
                <Input placeholder="请输入手机号" />
              </FormItem>
              <FormItem label="邮箱" name="email">
                <Input placeholder="请输入邮箱" />
              </FormItem>
            </div>

            <div className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100 mt-4 flex items-center gap-2">
              <PlusIcon style={{ color: '#0075ff' }} />
              租户信息
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
              <FormItem label="套餐等级" name="tenantLevel" rules={[{ required: true, message: '请选择套餐等级' }]} className="md:col-span-2">
                <Select placeholder="请选择套餐等级" filterable onChange={handleMergePackageChange}>
                  {packageLevels.map((p) => (
                    <Option key={p.id} value={p.id}>
                      {p.levelName}（{p.levelType === 1 ? '企业' : '个人'}，¥{p.levelSalesPrice}/年）
                    </Option>
                  ))}
                </Select>
              </FormItem>
            </div>

            {mergePackageCard}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
              <FormItem label="租户名称" name="tenantName" rules={[{ required: true, message: '请输入租户名称' }]} className="md:col-span-2">
                <Input placeholder="请输入租户名称（企业名称等）" />
              </FormItem>
              <FormItem label="租户类型" name="tenantType" rules={[{ required: true, message: '请选择租户类型' }]}>
                <Select placeholder="请选择租户类型" disabled={!!mergeSelectedPackage}>
                  <Option value={1}>企业</Option>
                  <Option value={2}>个人</Option>
                </Select>
              </FormItem>
              <FormItem label="租用价格" name="tenantPrice" rules={[{ required: true, message: '请选择套餐等级' }]}>
                <Input placeholder="选择套餐后自动填充" disabled value={mergeSelectedPackage ? String(mergeSelectedPackage.levelSalesPrice) : ''} />
              </FormItem>
              <FormItem label="邀请时间" name="invitedTime" rules={[{ required: true, message: '请选择邀请时间' }]} className="md:col-span-2">
                <DatePicker placeholder="请选择邀请时间" format="YYYY-MM-DD HH:mm:ss" />
              </FormItem>
              <FormItem label="租用时间" name="dateRange" rules={[{ required: true, message: '请选择租用时间' }]} className="md:col-span-2">
                <DateRangePicker
                  placeholder={['开始时间', '结束时间']}
                  format="YYYY-MM-DD HH:mm:ss"
                />
              </FormItem>
            </div>
          </div>
        </Form>
      </Dialog>
    </div>
  );
}